<template>
  <div v-if="storeData" class="sbd">
    <!-- Hiding Search Options As Per Requirement -->
    <div v-show="false" class="sbd__header">
      <div class="sbd__input-wrapper">
        <!-- Search Icon inside input -->
        <svg class="sbd__search-icon">
          <use xlink:href="#icon-search"></use>
        </svg>

        <!-- Close Icon inside input -->
        <svg v-if="showCloseSearchIcon" class="sbd__search-close-icon" @click="closeSearch">
          <use xlink:href="#close"></use>
        </svg>

        <!-- Google autocomplete input box -->
        <vue-google-autocomplete 
          ref="searchInput"
          id="map" 
          classname="sbd__input" 
          types="(regions)"
          country="US"
          :placeholder="data.searchPlaceholder" 
          @inputChange="setInputChange"
          @focus="setInputFocus"
          @blur="setInputBlur"
          v-on:placechanged="getAddressData"
        >
        </vue-google-autocomplete>

        <!-- Show my current location -->
        <div v-show="showMyLoc" class="sbd__current-location" v-on:click="getCurrentLocation">
          <svg class="sbd__navigate-icon">
            <use xlink:href="#icon-navigate"></use>
          </svg>
          <span>Use My Current Location</span>
        </div>
      </div>

      <!-- Distance dropdown -->
      <div class="sbd__select-wrapper">
        <CustomSelect
          ref="radiusDropdown"
          :options="radiusOption"
          optionPrefix="Distance: Within"
          optionSuffix="mi"
          placeholder="Select Your Distance"
        />
      </div>
    </div>
    
    <div :class="['sbd__btn-wrapper',{'is--map': isMapView}]">
      <!-- Section label -->
      <h3 class="section__label sbd__title">{{ sectionLabel }}</h3>
      <div class="sbd__btns">
        <!-- List view btn -->
        <button :class="['sbd__btn',{'active':isListView},'list-cta']" @click="changeStoreView('list')">
          <svg class="sbd__btn-icon">
            <use xlink:href="#icon-hamburger"></use>
          </svg>
          <span>List View</span>
        </button>

        <!-- Map view btn -->
        <button :class="['sbd__btn',{'active':isMapView},'map-cta']" @click="changeStoreView('map')">
          <svg class="sbd__btn-icon">
            <use xlink:href="#icon-pin"></use>
          </svg>
          <span>Map View</span>
        </button>
      </div>
    </div>

    <!-- Studio / Outpost Wrapper -->
    <div v-if="isListView && !isSearchActive">
      <div class="sbd__studios">
        <StudioGrid
          v-for="studio in studioData"
          :key="studio.name"
          :studio="studio"
        />
      </div>
      <div class="sbd__outposts-wrapper">
        <h3 class="section__label">{{data.outpostTitle}}</h3>
        <OutpostGrid
          :outposts="outpostData"
          :isSearch="false"
        />
      </div>
    </div>
    
    <!-- Search view -->
    <div v-if="isListView && isSearchActive" class="sbd__searchdata-wrapper">
      <OutpostGrid
        v-if="searchedStoreData.length"
        :outposts="searchedStoreData"
        :isSearch="true"
      />
      <div v-else class="sbd__no-data">
        <svg @click="toggleDropdown" class="sbd__no-data-icon">
          <use xlink:href="#icon-cross-new"></use>
        </svg>
        <p class="sbd__no-data-title">No Stores location were found</p>
        <p class="sbd__no-data-desc">Please try another input address to search for locations.</p>
      </div>
    </div>

    <!-- Map wrapper -->
    <div v-show="isMapView">
      <div data-stockist-widget-tag="u4881" class="sbd__no-data">Loading store locator</div>
    </div>
  </div>
  <div v-else>
    <StoreLoaderDesktop/>
    <StoreLoaderMobile/>
  </div>
</template>

<script setup>
  import { computed, toRefs, ref, onBeforeMount,watch,reactive, onMounted } from 'vue';
  import CustomSelect from '../global/custom-select.vue'
  import VueGoogleAutocomplete from "vue-google-autocomplete";
  import StudioGrid from './components/studio-grid.vue';
  import OutpostGrid from './components/outpost-grid.vue';
  import StoreLoaderDesktop from './components/store-loader-desktop.vue';
  import StoreLoaderMobile from './components/store-loader-mobile.vue';

  const props = defineProps({
    data: {
      type: Array,
      required: true,
    }
  })

  const { data } = toRefs(props)
  var storeData = ref(null)
  const searchedStoreData = ref(null)
  const searchInput = ref(null)
  const showMyLoc = ref(false)
  const showCloseSearchIcon = ref(false)
  const latitude = ref(null)
  const longitude = ref(null)
  const currentActiveView = ref('list')
  const mapLoaded = ref(false)
  const radiusDropdown = ref(null)
  const radiusOption = reactive(['25', '50', '75', '100','500'])

  onBeforeMount(async() => {
    await fetch(`https://stockist.co/api/v1/u4881/locations/all`)
    .then(resp => resp.text())
    .then(data => {
      storeData.value = JSON.parse(data)
    })    
  })


  const isMapView = computed(() => {
    return currentActiveView.value === 'map'
  })

  const isListView = computed(() => {
    return currentActiveView.value === 'list'
  })

  const sectionLabel = computed(() => {
    if(isMapView.value){
      return 'Mountain Studios & Outpost'
    }else if(isListView.value && isSearchActive.value){
      return 'All Stores by Location'
    }else{
      return 'Mountain Studios'
    }
  })

  const studioData = computed(() => {
    var data;
    storeData.value && (data = storeData.value.filter(store => store.filters.length && store.filters[0].name.includes('Stio Mountain Studio')))

    return data;
  })

  const outpostData = computed(() => {
    var data;
    storeData.value && (data = storeData.value.filter(store => store.filters.length && store.filters[0].name.includes('Authorized Retail Location')))

    return data;
  })

  const isSearchActive = computed(() => {
    return !(searchedStoreData.value === null)
  })

  const selectedRadius = computed(() => {
    return radiusDropdown.value ? radiusDropdown.value.selectedOption : radiusDropdown.value;
  })

  const changeStoreView = async (view) => {
    currentActiveView.value = view;

    // Load map script only when switching to map view for the first time
    if (view === 'map' && !mapLoaded.value) {
      // await waitForWidgetContainer();
      await loadScript('//stockist.co/embed/v1/widget.min.js');
      mapLoaded.value = true;
    }
  }

  const setInputChange = (value) => {
    if(value.newVal.length){
      showMyLoc.value = false;
      showCloseSearchIcon.value = true;
    }else{
      showMyLoc.value = true;
      showCloseSearchIcon.value = false;
    }
  }

  const setInputFocus = () => {
    if(searchInput.value.autocompleteText.length === 0){
      showMyLoc.value = true;
    }else{
      showMyLoc.value = false;
    }
  }

  const setInputBlur = (e) => {
    setTimeout(() =>{
      showMyLoc.value = false;
    },150)
  }

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) =>{ 
        showMyLoc.value = false;
        searchInput.value.update('My Location');
        latitude.value = position.coords.latitude;
        longitude.value = position.coords.longitude
      },(error) => {console.log(error)});
    }
  }

  const getAddressData = (addressData) => {
    latitude.value = addressData.latitude;
    longitude.value = addressData.longitude;    
  }

  const closeSearch = () => {
    searchedStoreData.value = null
    latitude.value = null;
    longitude.value = null;
    searchInput.value.clear();
  }

  watch([latitude, longitude,selectedRadius], async([newLatitudeVal, newLongitudeVal,newRadiusVal], [oldLatitudeVal, oldLongitudeVal, oldRadiusVal]) => {
    let url;
    if(newRadiusVal && newLatitudeVal && newLongitudeVal){
      url = `https://stockist.co/api/v1/u4881/locations/search?latitude=${newLatitudeVal}&longitude=${newLongitudeVal}&distance=${newRadiusVal}`
    }else if(newLatitudeVal && newLongitudeVal && newLatitudeVal != oldLatitudeVal && newLongitudeVal != oldLongitudeVal){
      url = `https://stockist.co/api/v1/u4881/locations/search?latitude=${newLatitudeVal}&longitude=${newLongitudeVal}`;
    }

    if(url){
      await fetch(url)
      .then(resp => resp.text())
      .then(data => JSON.parse(data))
      .then(parsedData => {
        searchedStoreData.value = parsedData.locations
      })
    }
  });
</script>